/**
 * Migrates section HTML to use client bindings (pb-client:// hrefs and
 * data-pb-client / data-pb-client-text markers) by detecting hardcoded
 * values that match the current clientInfo.
 *
 * Pure functions — callers are responsible for writing results back to state.
 * Idempotent: rerunning on already-migrated HTML produces no diff.
 */

import { normalizeClientInfo } from './client-info.js';
import { walkTextNodes, wrapTextMatch, wrapBoundedTextMatch, escapeRegExp } from './text-walker.js';
import { iterateAllSections } from './iterate-sections.js';

const SOCIAL_KEYS = ['facebook', 'instagram', 'linkedin', 'x', 'youtube', 'yelp'];

function digits(str) {
  return String(str || '').replace(/\D+/g, '');
}

function stripProtocol(url) {
  return String(url || '')
    .replace(/^https?:\/\//i, '')
    .replace(/\/+$/g, '');
}

function sameUrl(a, b) {
  if (!a || !b) return false;
  return stripProtocol(a).toLowerCase() === stripProtocol(b).toLowerCase();
}

// Normalize a host or full URL for comparison: strip protocol, trailing slash,
// www. prefix, lowercase. Used by both the href domain rule and the anchor-text
// domain match so example.com / www.example.com / https://www.example.com/ all
// compare equal regardless of which form lives in client.domain.
//
// INTENTIONAL: paths/queries/fragments are NOT stripped — stripProtocol leaves
// them in place. "https://example.com/about" becomes "example.com/about", which
// does NOT equal "example.com", so internal page links never get rewritten to
// pb-client://domain. The domain binding is for root-URL references only.
function domainKey(value) {
  return stripProtocol(value)
    .toLowerCase()
    .replace(/^www\./, '');
}

function emptyReport() {
  return {
    counts: {
      phone: 0,
      email: 0,
      domain: 0,
      mapLink: 0,
      social: 0,
      address: 0,
      name: 0,
      tagline: 0
    },
    skipped: []
  };
}

function bumpCount(report, key) {
  if (SOCIAL_KEYS.includes(key)) {
    report.counts.social++;
    return;
  }
  if (
    key === 'addressBlock' ||
    key === 'addressLineOne' ||
    key === 'addressLineTwo' ||
    key === 'addressCity' ||
    key === 'addressCounty' ||
    key === 'addressPostcode'
  ) {
    report.counts.address++;
    return;
  }
  if (key === 'phoneFormatted') {
    report.counts.phone++;
    return;
  }
  if (key in report.counts) {
    report.counts[key]++;
  }
}

function normalizeMigrationArgs(projectNameOrOptions, options = {}) {
  if (typeof projectNameOrOptions === 'string') {
    return {
      projectName: projectNameOrOptions,
      options: options && typeof options === 'object' ? options : {}
    };
  }
  const resolvedOptions =
    projectNameOrOptions && typeof projectNameOrOptions === 'object' ? projectNameOrOptions : {};
  return { projectName: 'Untitled', options: resolvedOptions };
}

// --- Text-rule helpers (one per non-trivial text matcher) ---
//
// All text helpers take (textNode, client, source). When `source` is provided,
// each helper first tries to match the template's ORIGINAL values (sourceProfile
// variants) and binds them, displaying the CURRENT client value. That is the
// rename-and-bind path. If no source variant matches, the helper falls back to
// the current-value-only path (pure bind, no rename).

function applyPhoneText(textNode, client, source) {
  // Rename pass: any source phone variant → bind with new phoneFormatted shown.
  const displayValue = client.phoneFormatted || client.phone || '';
  const sourceVariants = source?.phoneVariants || [];
  for (const variant of sourceVariants) {
    if (!variant) continue;
    if (variant === client.phoneFormatted || variant === client.phone) continue;
    if (wrapTextMatch(textNode, variant, 'phoneFormatted', displayValue)) return true;
  }
  // Bind pass: current display forms.
  for (const form of [client.phoneFormatted, client.phone]) {
    if (!form) continue;
    if (wrapTextMatch(textNode, form, 'phoneFormatted', form)) return true;
  }
  return false;
}

function applyEmailText(textNode, client, source) {
  // Rename pass: source email → bind with new email shown.
  const newEmail = client.email || '';
  const sourceEmail = clean(source?.email);
  if (sourceEmail && sourceEmail.toLowerCase() !== newEmail.toLowerCase()) {
    if (wrapCaseInsensitive(textNode, sourceEmail, 'email', newEmail || sourceEmail)) return true;
  }
  // Bind pass: current email literal.
  if (client.emailLower) {
    return wrapCaseInsensitive(textNode, client.email, 'email', client.email);
  }
  return false;
}

function wrapCaseInsensitive(textNode, needle, bindingKey, displayValue) {
  if (!needle) return false;
  const text = textNode.nodeValue;
  const idx = text.toLowerCase().indexOf(needle.toLowerCase());
  if (idx === -1) return false;
  const matched = text.slice(idx, idx + needle.length);
  return wrapTextMatch(
    textNode,
    matched,
    bindingKey,
    displayValue != null ? displayValue : matched
  );
}

function applyAddressBlock(textNode, client, source) {
  // Try source full-address block first if available.
  const sourceBlock = source?.addressBlock || '';
  const newBlock = client.addressBlock || '';
  if (sourceBlock && sourceBlock !== newBlock) {
    const text = textNode.nodeValue;
    if (text.replace(/\s+/g, ' ').trim() === sourceBlock) {
      replaceWholeTextWithBinding(textNode, 'addressBlock', newBlock || sourceBlock);
      return true;
    }
    if (text.includes(sourceBlock)) {
      return wrapTextMatch(textNode, sourceBlock, 'addressBlock', newBlock || sourceBlock);
    }
  }
  // Bind pass: current block literal.
  if (newBlock) {
    const text = textNode.nodeValue;
    if (text.replace(/\s+/g, ' ').trim() === newBlock) {
      replaceWholeTextWithBinding(textNode, 'addressBlock', newBlock);
      return true;
    }
    if (text.includes(newBlock)) {
      return wrapTextMatch(textNode, newBlock, 'addressBlock', newBlock);
    }
  }
  return false;
}

function replaceWholeTextWithBinding(textNode, bindingKey, displayValue) {
  const doc = textNode.ownerDocument;
  const span = doc.createElement('span');
  span.setAttribute('data-pb-client', bindingKey);
  span.textContent = displayValue;
  textNode.parentNode.replaceChild(span, textNode);
}

// Visible-domain matching: prefer wrapping `www.${domain}` when present so we
// don't leave a stale "www." outside the binding. Falls back to bare domain.
function applyDomainText(textNode, client, source) {
  // Rename pass: source domain → bind with new domain shown.
  const sourceDomain = (source?.sourceDomain || '')
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./, '');
  if (sourceDomain && sourceDomain !== (client.domain || '').replace(/^www\./, '')) {
    if (tryWrapDomain(textNode, sourceDomain, client.domain || sourceDomain)) return true;
  }
  // Bind pass: current domain.
  if (client.domain) {
    return tryWrapDomain(textNode, client.domain, client.domain);
  }
  return false;
}

function tryWrapDomain(textNode, needle, displayValue) {
  const bare = needle.replace(/^www\./, '');
  if (!bare) return false;
  const wwwForm = `www.${bare}`;
  if (
    wrapBoundedTextMatch(
      textNode,
      wwwForm,
      'domain',
      `www.${(displayValue || '').replace(/^www\./, '')}`
    )
  )
    return true;
  return wrapBoundedTextMatch(textNode, bare, 'domain', (displayValue || '').replace(/^www\./, ''));
}

function clean(v) {
  return typeof v === 'string' ? v.trim() : '';
}

function cleanList(values) {
  return (Array.isArray(values) ? values : [values]).map(clean).filter(Boolean);
}

function uniqueClean(values) {
  return [...new Set(cleanList(values))];
}

function applyAddressPartText(textNode, client, source, fieldKey, sourceKey) {
  // fieldKey: 'lineOne' | 'lineTwo' | 'city' | 'county' | 'postcode'
  // sourceKey: index into source.addressLines (when source has structured lines)
  const newValue = client.address[fieldKey];
  // Rename pass: try source variants
  const sourceLines = Array.isArray(source?.addressLines) ? source.addressLines : [];
  const sourceValue = source?.addressParts?.[fieldKey] || sourceLines[sourceKey] || '';
  if (sourceValue && sourceValue !== newValue) {
    if (
      wrapBoundedTextMatch(
        textNode,
        sourceValue,
        `address${capitalize(fieldKey)}`,
        newValue || sourceValue
      )
    )
      return true;
  }
  // Bind pass: current value
  if (newValue) {
    return wrapBoundedTextMatch(textNode, newValue, `address${capitalize(fieldKey)}`, newValue);
  }
  return false;
}

function capitalize(s) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function applyNameText(textNode, client, source) {
  const newName = client.name || '';
  const sourceNames = collectSourceNames(source);
  for (const variant of sourceNames) {
    if (!variant || variant === newName) continue;
    if (wrapBoundedTextMatch(textNode, variant, 'name', newName || variant)) return true;
  }
  if (newName) {
    return wrapBoundedTextMatch(textNode, newName, 'name', newName);
  }
  return false;
}

function collectSourceNames(source) {
  const out = [];
  if (!source) return out;
  if (source.businessName) out.push(clean(source.businessName));
  if (Array.isArray(source.businessNameVariants)) {
    for (const v of source.businessNameVariants) {
      const c = clean(v);
      if (c && !out.includes(c)) out.push(c);
    }
  }
  return out;
}

function applyTaglineText(textNode, client, source) {
  const newValue = client.tagline || '';
  const sourceValue = clean(source?.tagline);
  if (sourceValue && sourceValue !== newValue) {
    if (wrapBoundedTextMatch(textNode, sourceValue, 'tagline', newValue || sourceValue))
      return true;
  }
  if (newValue) {
    return wrapBoundedTextMatch(textNode, newValue, 'tagline', newValue);
  }
  return false;
}

// --- Rule registries ---
//
// Each HREF_RULES entry rewrites an <a href="…"> into <a href="pb-client://KEY">
// and may also tag visible anchor text with data-pb-client-text="KEY".
//
// Each `test(href, c, source)` returns truthy when either:
//   - the href matches a CURRENT client value (pure bind), or
//   - the href matches a SOURCE-PROFILE variant (rename — e.g. an old
//     tel:9162340012 from the template's source profile is recognized and
//     redirected to the new pb-client://phone marker).
const HREF_RULES = [
  {
    key: 'phone',
    test: (href, c, source) => {
      if (!/^tel:/i.test(href)) return false;
      const hrefDigits = digits(href.slice(4));
      if (!hrefDigits) return false;
      if (c.phoneDigits && hrefDigits === c.phoneDigits) return true;
      const sourcePhones = source?.phoneDigitsSet;
      return !!(sourcePhones && sourcePhones.has(hrefDigits));
    },
    // Recognize the anchor's visible text as a phone form whether it matches the
    // CURRENT client phone or the template's SOURCE phone variants. Without the
    // source check, a renamed source-phone anchor (e.g. "(916) 234-0012" text)
    // would leave data-pb-client-text unset, and the text-pass fixpoint would
    // then wrap that text again inside the anchor → double bump + nested span.
    anchorTextKey: (anchor, c, source) => {
      if (anchor.children.length) return null;
      const text = (anchor.textContent || '').trim();
      if (!text) return null;
      const textDigits = digits(text);
      const isDigitsOnly = /^\+?\d[\d\s()+-]*$/.test(text);
      const matchesClient = !!c.phoneDigits && textDigits === c.phoneDigits;
      const matchesSource = !!source?.phoneDigitsSet && source.phoneDigitsSet.has(textDigits);
      if (!matchesClient && !matchesSource) return null;
      return isDigitsOnly ? 'phone' : 'phoneFormatted';
    }
  },
  {
    key: 'email',
    test: (href, c, source) => {
      if (!/^mailto:/i.test(href)) return false;
      const hrefEmail = href.slice(7).split('?')[0].toLowerCase();
      if (!hrefEmail) return false;
      if (c.emailLower && hrefEmail === c.emailLower) return true;
      const sourceEmail = clean(source?.email).toLowerCase();
      return !!(sourceEmail && hrefEmail === sourceEmail);
    },
    anchorTextKey: (anchor, c, source) => {
      if (anchor.children.length) return null;
      const text = (anchor.textContent || '').trim().toLowerCase();
      if (!text) return null;
      if (c.emailLower && text === c.emailLower) return 'email';
      const sourceEmail = clean(source?.email).toLowerCase();
      if (sourceEmail && text === sourceEmail) return 'email';
      return null;
    }
  },
  {
    key: 'domain',
    test: (href, c, source) => {
      if (!/^https?:\/\//i.test(href)) return false;
      const key = domainKey(href);
      if (!key) return false;
      if (c.domainKey && key === c.domainKey) return true;
      const sourceKey = source?.sourceDomainKey;
      return !!(sourceKey && key === sourceKey);
    },
    anchorTextKey: (anchor, c, source) => {
      if (anchor.children.length) return null;
      const text = (anchor.textContent || '').trim();
      if (!text) return null;
      const textKey = domainKey(text);
      if (!textKey) return null;
      if (c.domainKey && textKey === c.domainKey) return 'domain';
      if (source?.sourceDomainKey && textKey === source.sourceDomainKey) return 'domain';
      return null;
    }
  },
  {
    key: 'mapLink',
    test: (href, c, source) =>
      !!(c.address.mapLink && sameUrl(href, c.address.mapLink)) ||
      !!(source?.mapLink && sameUrl(href, source.mapLink))
  },
  { key: 'facebook', test: socialTest('facebook') },
  { key: 'instagram', test: socialTest('instagram') },
  { key: 'linkedin', test: socialTest('linkedin') },
  { key: 'x', test: socialTest('x') },
  { key: 'youtube', test: socialTest('youtube') },
  { key: 'yelp', test: socialTest('yelp') }
];

// Social URLs are matched current-first, then any source-profile variant.
// Templates often ship with placeholder social URLs (e.g. facebook.com/example)
// — match those too so they get rebound to whatever the user enters.
function socialTest(socialKey) {
  return (href, c, source) => {
    if (c.social[socialKey] && sameUrl(href, c.social[socialKey])) return true;
    const sourceVariants = source?.socialUrls?.[socialKey];
    if (!sourceVariants) return false;
    for (const variant of sourceVariants) {
      if (variant && sameUrl(href, variant)) return true;
    }
    return false;
  };
}

// Each TEXT_RULES entry wraps the first matching slice of a text node in
// <span data-pb-client="KEY">. Idempotency = walkTextNodes already skips
// inside data-pb-client / data-pb-client-text ancestors.
//
// ORDER MATTERS. The fixpoint walker tries rules top-to-bottom per text node
// and stops at the first match. Higher-specificity rules (phone, email,
// addressBlock, address parts) come before lower-specificity rules (name,
// tagline, domain) so a phone number embedded in a sentence is never claimed
// by a name that contains digits, and a city/region name doesn't shadow the
// business name. Mixed-value text nodes are still fully covered because
// subsequent fixpoint passes re-attempt every rule on the new remnant nodes
// created by wrapping.
const TEXT_RULES = [
  { key: 'phoneFormatted', apply: applyPhoneText },
  { key: 'email', apply: applyEmailText },
  { key: 'addressBlock', apply: applyAddressBlock },
  {
    key: 'addressLineOne',
    apply: (n, c, s) => applyAddressPartText(n, c, s, 'lineOne', 0),
    gated: 'includeAddressParts'
  },
  {
    key: 'addressLineTwo',
    apply: (n, c, s) => applyAddressPartText(n, c, s, 'lineTwo', 1),
    gated: 'includeAddressParts'
  },
  {
    key: 'addressCity',
    apply: (n, c, s) => applyAddressPartText(n, c, s, 'city', 2),
    gated: 'includeAddressParts'
  },
  {
    key: 'addressCounty',
    apply: (n, c, s) => applyAddressPartText(n, c, s, 'county', 3),
    gated: 'includeAddressParts'
  },
  {
    key: 'addressPostcode',
    apply: (n, c, s) => applyAddressPartText(n, c, s, 'postcode', 4),
    gated: 'includeAddressParts'
  },
  { key: 'name', apply: applyNameText },
  { key: 'tagline', apply: applyTaglineText },
  { key: 'domain', apply: applyDomainText }
];

// Returns true iff at least one anchor was bound.
function applyHrefRules(root, client, source, report) {
  let anyChange = false;
  for (const anchor of Array.from(root.querySelectorAll('a[href]'))) {
    const href = anchor.getAttribute('href') || '';
    if (href.startsWith('pb-client://')) continue; // idempotent
    for (const rule of HREF_RULES) {
      if (!rule.test(href, client, source)) continue;
      anchor.setAttribute('href', `pb-client://${rule.key}`);
      bumpCount(report, rule.key); // ONE bump per anchor (href + optional anchor-text)
      const txtKey = rule.anchorTextKey?.(anchor, client, source);
      if (txtKey) anchor.setAttribute('data-pb-client-text', txtKey);
      anyChange = true;
      break;
    }
  }
  return anyChange;
}

// Returns true iff at least one text node was wrapped.
//
// Fixpoint walker: one text node may carry multiple distinct values
// (e.g. "Call 555-1234 or email hi@acme.com from Acme today"). After wrapping
// the first match, the surrounding remnant text nodes are NEW nodes that the
// cached walkTextNodes list never visited — so we re-walk until a full pass
// produces no changes. Idempotency guarantees convergence: each pass either
// consumes at least one new node or terminates.
function applyTextRules(root, client, source, report, migrationOptions) {
  const MAX_PASSES = 32; // generous safety net; expected: 1-4
  const activeRules = TEXT_RULES.filter(
    (rule) => !rule.gated || migrationOptions[rule.gated] !== false
  );
  let anyChange = false;
  let passDidWork = true;
  let pass = 0;
  while (passDidWork && pass < MAX_PASSES) {
    passDidWork = false;
    walkTextNodes(root, (textNode) => {
      if (!textNode.parentNode) return;
      for (const rule of activeRules) {
        if (rule.apply(textNode, client, source)) {
          bumpCount(report, rule.key);
          passDidWork = true;
          anyChange = true;
          return;
        }
      }
    });
    pass++;
  }
  return anyChange;
}

function resolveAddressBlock(client) {
  return [
    client.address.lineOne,
    [client.address.city, client.address.county].filter(Boolean).join(', '),
    client.address.postcode
  ]
    .filter(Boolean)
    .join(' ')
    .trim();
}

function buildScanClient(clientInfo, projectName) {
  const normalized = normalizeClientInfo(clientInfo, projectName);
  // Pre-compute matcher conveniences once per migration.
  normalized.phoneDigits = digits(normalized.phone);
  normalized.emailLower = (normalized.email || '').toLowerCase();
  normalized.domainKey = domainKey(normalized.domain);
  normalized.addressBlock = resolveAddressBlock(normalized);
  return normalized;
}

// Build a scan-ready snapshot of the template's source profile (the ORIGINAL
// values the template shipped with, e.g. "Blade Works Studio", a 916-area phone,
// the template's source domain). Pre-computes matcher-friendly forms so the
// rules don't recompute per anchor / per text node. Returns null when the
// project has no source profile (legacy projects), in which case the migrator
// behaves as bind-only — current behavior.
function buildScanSource(sourceProfile) {
  if (!sourceProfile || typeof sourceProfile !== 'object') return null;
  const phoneVariants = Array.isArray(sourceProfile.phoneVariants)
    ? sourceProfile.phoneVariants.map(clean).filter(Boolean)
    : [];
  const phoneDigitsSet = new Set(phoneVariants.map(digits).filter(Boolean));
  const sourceDomain = clean(sourceProfile.sourceDomain);
  const addressParts = normalizeSourceAddressParts(sourceProfile);
  const addressLines = [
    addressParts.lineOne,
    addressParts.lineTwo,
    addressParts.city,
    addressParts.county,
    addressParts.postcode
  ];
  const addressBlock =
    addressLines.map(clean).filter(Boolean).join(' ').trim() || clean(sourceProfile.address);
  const socialUrls = collectSourceSocialUrls(sourceProfile);
  return {
    businessName: clean(sourceProfile.businessName),
    businessNameVariants: Array.isArray(sourceProfile.businessNameVariants)
      ? sourceProfile.businessNameVariants.map(clean).filter(Boolean)
      : [],
    tagline: clean(sourceProfile.tagline),
    email: clean(sourceProfile.email),
    phoneVariants,
    phoneDigitsSet,
    addressLines,
    addressParts,
    addressBlock,
    mapLink: clean(
      sourceProfile.mapLink || sourceProfile.addressMapLink || sourceProfile.address?.mapLink
    ),
    sourceDomain,
    sourceDomainKey: domainKey(sourceDomain),
    socialUrls
  };
}

function normalizeSourceAddressParts(sourceProfile) {
  let values = Array.isArray(sourceProfile?.addressLines)
    ? sourceProfile.addressLines.map(clean)
    : [];
  if (!values.length && typeof sourceProfile?.address === 'string') {
    values = sourceProfile.address.split(',').map(clean);
  }

  if (values.length >= 5) {
    return {
      lineOne: values[0] || '',
      lineTwo: values[1] || '',
      city: values[2] || '',
      county: values[3] || '',
      postcode: values[4] || ''
    };
  }

  const compact = values.filter(Boolean);
  if (compact.length >= 4) {
    return {
      lineOne: compact[0] || '',
      lineTwo: '',
      city: compact[compact.length - 3] || '',
      county: compact[compact.length - 2] || '',
      postcode: compact[compact.length - 1] || ''
    };
  }

  return {
    lineOne: compact[0] || '',
    lineTwo: compact[1] || '',
    city: compact[2] || '',
    county: compact[3] || '',
    postcode: compact[4] || ''
  };
}

function collectSourceSocialUrls(sourceProfile) {
  // sourceProfile may store socials as either { social: { facebook: 'url' } }
  // (mirroring clientInfo) or as a list of URLs. Normalize to per-platform arrays.
  const out = { facebook: [], instagram: [], linkedin: [], x: [], youtube: [], yelp: [] };
  const social =
    sourceProfile.social && typeof sourceProfile.social === 'object' ? sourceProfile.social : {};
  for (const key of Object.keys(out)) {
    const v = social[key];
    if (typeof v === 'string' && v) out[key].push(v.trim());
    else if (Array.isArray(v)) out[key].push(...v.map(clean).filter(Boolean));
  }
  // Catch-all: socialUrls array w/ host detection.
  const flat = Array.isArray(sourceProfile.socialUrls) ? sourceProfile.socialUrls : [];
  for (const url of flat) {
    const lower = clean(url).toLowerCase();
    if (!lower) continue;
    if (lower.includes('facebook.com') || lower.includes('fb.com')) out.facebook.push(url);
    else if (lower.includes('instagram.com')) out.instagram.push(url);
    else if (lower.includes('linkedin.com')) out.linkedin.push(url);
    else if (lower.includes('twitter.com') || lower.includes('x.com')) out.x.push(url);
    else if (lower.includes('youtube.com') || lower.includes('youtu.be')) out.youtube.push(url);
    else if (lower.includes('yelp.com')) out.yelp.push(url);
  }
  return out;
}

/**
 * Migrate a single section's HTML string. Returns { html, changed, report }.
 */
export function migrateSectionHTMLToBindings(
  html,
  clientInfo,
  projectNameOrOptions = 'Untitled',
  options = {}
) {
  const { projectName, options: migrationOptions } = normalizeMigrationArgs(
    projectNameOrOptions,
    options
  );
  const report = emptyReport();
  if (!html || typeof html !== 'string') return { html: html || '', changed: false, report };
  if (typeof DOMParser === 'undefined') return { html, changed: false, report };

  const client = buildScanClient(clientInfo, projectName);
  const source = buildScanSource(migrationOptions.sourceProfile);
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  if (!root) return { html, changed: false, report };

  const hrefChanged = applyHrefRules(root, client, source, report);
  const textChanged = applyTextRules(root, client, source, report, migrationOptions);
  const changed = hrefChanged || textChanged;

  return {
    html: changed ? root.innerHTML : html,
    changed,
    report
  };
}

/**
 * Iterate every section in the given state and run migrateSectionHTMLToBindings
 * on its HTML. Returns the aggregated report and a list of per-section paths
 * the caller should write back via store.update.
 *
 * Does NOT mutate state itself — caller is responsible for writing back.
 */
export function migrateAllSectionsInState(state, options = {}) {
  const totals = emptyReport();
  const updates = []; // { path, html }
  const changedSections = [];
  const details = [];

  if (!state) {
    return { updates, report: totals, changedSections, total: 0, details };
  }
  const projectName = state.projectName || 'Untitled';
  const clientInfo = state.clientInfo;
  // Auto-thread the project's source profile (template's original brand info)
  // so resync renames Blade Works Studio → user's name. Caller can override.
  const mergedOptions =
    options.sourceProfile === undefined && state.sourceClientProfile
      ? { ...options, sourceProfile: state.sourceClientProfile }
      : options;

  iterateAllSections(state, (ctx) => {
    const { html, changed, report } = migrateSectionHTMLToBindings(
      ctx.html,
      clientInfo,
      projectName,
      mergedOptions
    );
    if (!changed) return;
    const sectionKey =
      ctx.instanceId || ctx.section?.sectionId || ctx.section?.catalogKey || ctx.sectionPath;
    updates.push({ path: ctx.sectionPath, html, instanceId: ctx.instanceId });
    mergeReport(totals, report);
    changedSections.push(sectionKey);
    details.push({
      sectionKey,
      instanceId: ctx.instanceId,
      path: ctx.sectionPath,
      counts: { ...report.counts }
    });
  });

  return { updates, report: totals, changedSections, total: changedSections.length, details };
}

/**
 * Build a source-profile-shaped snapshot from the current Client Info. Used by
 * the Resync button to roll state.sourceClientProfile forward after a sync, so
 * the next edit-then-resync renames from "what was just synced" → "newer".
 *
 * Preserves any merge-relevant fields from the prior source profile (logoSrc,
 * logoSelector, etc.) the migrator doesn't manage — only client-facing values
 * are overwritten.
 */
export function clientInfoToSourceProfile(clientInfo, priorSourceProfile) {
  const c = clientInfo || {};
  const addr = c.address || {};
  const social = c.social || {};
  const phone = clean(c.phone) || clean(c.phoneFormatted);
  const name = clean(c.name);
  const priorNameVariants = Array.isArray(priorSourceProfile?.businessNameVariants)
    ? priorSourceProfile.businessNameVariants
    : [];
  const priorPhoneVariants = Array.isArray(priorSourceProfile?.phoneVariants)
    ? priorSourceProfile.phoneVariants
    : [];
  const next = {
    ...(priorSourceProfile && typeof priorSourceProfile === 'object' ? priorSourceProfile : {}),
    businessName: name,
    businessNameVariants: uniqueClean([name, ...priorNameVariants]),
    tagline: clean(c.tagline),
    email: clean(c.email),
    phoneVariants: uniqueClean([...phoneFormatsFor(phone), ...priorPhoneVariants]),
    addressLines: [addr.lineOne, addr.lineTwo, addr.city, addr.county, addr.postcode].map(clean),
    address: [addr.lineOne, addr.lineTwo, addr.city, addr.county, addr.postcode]
      .map(clean)
      .filter(Boolean)
      .join(', '),
    mapLink: clean(addr.mapLink),
    sourceDomain: clean(c.domain).replace(/^https?:\/\//i, ''),
    social: {
      facebook: clean(social.facebook),
      instagram: clean(social.instagram),
      linkedin: clean(social.linkedin),
      x: clean(social.x),
      youtube: clean(social.youtube),
      yelp: clean(social.yelp)
    }
  };
  return next;
}

function phoneFormatsFor(phone) {
  const raw = clean(phone);
  if (!raw) return [];
  const d = raw.replace(/\D+/g, '');
  const variants = new Set([raw]);
  if (d) variants.add(d);
  if (d.length === 10) {
    variants.add(`(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`);
    variants.add(`${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`);
    variants.add(`${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`);
    variants.add(`${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6)}`);
  }
  return [...variants];
}

function mergeReport(target, source) {
  for (const key of Object.keys(source.counts)) {
    target.counts[key] = (target.counts[key] || 0) + source.counts[key];
  }
  if (source.skipped && source.skipped.length) {
    target.skipped.push(...source.skipped);
  }
}
